/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { DeviceDetailsProps } from "../types";
import MA600G1AlarmTable from "../components/MA600G1AlarmTable";
import MA600G1RadioModuleAlarmTable from "../components/MA600G1RadioModuleAlarmTable";
import DetailRow from "../components/DetailRow";
import DasPowerDetectorChart from "./DasPowerDetectorChart";
import { Card, CardContent } from "../../../ui/card";

import {
  createPowerDetectorChartConfig,
  buildPowerDetectorParams,
  PowerDetectorParams,
} from "./dasChartUtils";

const AuDeviceDetails: React.FC<DeviceDetailsProps> = ({
  device,
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
  siteObjectKey,
}) => {
  const isDisconnected = statusLabel === "Disconnected";

  // Parse alarm data from attributes
  // The alarms data is expected to be in the device attributes as a JSON string or object
  let alarms: any[] = [];
  
  // Debug logging to help identify data format issues
  console.log("AU Device - parsedAttributes:", parsedAttributes);
  console.log("AU Device - device.equipmentType:", device.equipmentType);
  console.log("AU Device - devType:", parsedAttributes.devType);
  
  try {
    if (parsedAttributes.alarms) {
      const parsedAlarms = typeof parsedAttributes.alarms === 'string' 
        ? JSON.parse(parsedAttributes.alarms) 
        : parsedAttributes.alarms;
      
      // Convert alarms to array format
      if (Array.isArray(parsedAlarms)) {
        alarms = parsedAlarms;
      } else if (typeof parsedAlarms === 'object' && parsedAlarms !== null) {
        // Convert object to array format: { "alarmName": { ...props } } => [{ name: "alarmName", ...props }]
        alarms = Object.entries(parsedAlarms).map(([name, alarmData]: [string, any]) => ({
          name: name,
          ...(typeof alarmData === 'object' ? alarmData : {})
          
        }));
        console.log("AU Device - Converted alarms object to array:", alarms);
        console.log("AU Device - Sample alarm structure:", alarms[0]);
      } else {
        console.warn("AU Device - Parsed alarms is not an array or object:", parsedAlarms);
        alarms = [];
      }
      console.log("AU Device - Parsed alarms:", alarms);
    } else {
      console.warn("AU Device - No alarms data found in parsedAttributes");
    }
  } catch (error) {
    console.error("AU Device - Error parsing alarms:", error);
    alarms = [];
  }

  // Check if device is disabled
  // Device can be disabled via status or specific enabled flag
  const isDeviceDisabled = 
    statusLabel === "Disabled" ||
    device.status === "DISABLED" ||
    parsedAttributes.enabled === "false" ||
    parsedAttributes.enabled === false ||
    parsedAttributes.enabled === "0" ||
    parsedAttributes.enabled === 0;

  const devSerialNumber = parsedAttributes.devSerialNumber || device.serialNumber;

  // Extract TDD configuration from bandConfiguration
  const bandConfiguration = parsedAttributes.bandConfiguration || {};
  
  // Extract system info for radio signal information
  const systemInfo = parsedAttributes.systemInfo || {};
  
  // Extract combiners for radio interface modules
  const combiners = parsedAttributes.combiners || {};
  
  // Extract LAN connectivity for optical port power
  const lanConnectivity = parsedAttributes.lanConnectivity || {};

  // Debug: Log control mode values with full context
  console.log("=== AU Device Debug ===");
  console.log("parsedAttributes:", parsedAttributes);
  console.log("combiners object:", combiners);
  console.log("Object.keys(combiners):", Object.keys(combiners));
  console.log("Attenuation Control Mode RAW:", combiners.attenuationControlModeParam);
  console.log("Automatic Level Interval RAW:", combiners.automaticLevelIntervalParam);
  console.log("======================");

  // Tab state for Radio Interface Modules
  const [activeModuleTab, setActiveModuleTab] = useState(0);
  
  // Navigation state for sections
  const [activeSection, setActiveSection] = useState('rfTopology');
  
  // Dialog state for module details
  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState<number | null>(null);

  // State for fullscreen chart inside module dialog
  const [fullscreenChart, setFullscreenChart] = useState<'inputPower' | null>(null);
  const handleOpenFullscreen = () => setFullscreenChart('inputPower');
  const handleCloseFullscreen = () => setFullscreenChart(null);
  
  // Function to handle module card click
  const handleModuleClick = (moduleNum: number) => {
    setSelectedModule(moduleNum);
    setModuleDialogOpen(true);
  };
  
  // Function to close module dialog
  const handleCloseModuleDialog = () => {
    setModuleDialogOpen(false);
    setSelectedModule(null);
  };

  // Parent details drawer uses z-index 1400; keep child dialogs above it.
  const dialogLayerSx = {
    zIndex: 1600,
  };
  
  // Function to filter alarms for a specific module
  const getModuleAlarms = (moduleNum: number) => {
    if (!alarms || alarms.length === 0) return [];
    const modulePattern = new RegExp(`radioModule${moduleNum}`, 'i');
    return alarms.filter((alarm: any) => modulePattern.test(alarm.name));
  };

  // Chart configuration for selected module
  const moduleChartConfig = useMemo(() => {
    if (!selectedModule) return null;

    // Device serial number
    // Use siteObjectKey (MA6200 host serial) for KPI API calls, not the AU component's serial
    const serviceDeviceObjectKey = siteObjectKey || device.servicesDeviceObjectKey || device.serialNumber || '';
    
    // For AU device, the remoteEquipmentObjectKey format should match the device structure
    // Using the device objectKey or constructing it from available data
    const remoteEquipmentObjectKey = device.objectKey || device.serialNumber || '';
    
    // Build port indicators for the selected module's 4 ports
    const portNumbers = [1, 2, 3, 4];
    const portNames = portNumbers.map(p => `Port ${p}`);
    
    // UL power indicators for each port (Output Power)
    const ulIndicators = portNumbers.map(p => `radioModule${selectedModule}Port${p}OutputPower`);
    
    const ulParams: PowerDetectorParams = {
      remoteEquipmentObjectKey,
      indicators: ulIndicators,
      bandNames: portNames,
      bandNumbers: portNumbers,
      yAxisMin: -180,  // AU UL min power
      yAxisMax: 100,   // AU UL max power
    };

    const ulConfig = createPowerDetectorChartConfig('ul', ulParams);
    const ulChartParams = buildPowerDetectorParams(
      serviceDeviceObjectKey,
      remoteEquipmentObjectKey,
      ulIndicators
    );

    // DL power indicators for each port (Input Power)
    const dlIndicators = portNumbers.map(p => `radioModule${selectedModule}Port${p}InputPower`);
    
    const dlParams: PowerDetectorParams = {
      remoteEquipmentObjectKey,
      indicators: dlIndicators,
      bandNames: portNames,
      bandNumbers: portNumbers,
      yAxisMin: -100,  // AU DL min power (No Signal below this)
      yAxisMax: 50,    // AU DL max power (Overpower above this)
      divisor: 1000,   // Divide raw values by 1000 to convert to dBm
    };

    const dlConfig = createPowerDetectorChartConfig('dl', dlParams);
    const dlChartParams = buildPowerDetectorParams(
      serviceDeviceObjectKey,
      remoteEquipmentObjectKey,
      dlIndicators
    );

    return {
      ulConfig,
      ulChartParams,
      dlConfig,
      dlChartParams,
    };
  }, [selectedModule, device.serialNumber, device.objectKey]);

  // Helper functions to check if sections have data
  const hasBandConfigurationData = () => {
    return Object.keys(bandConfiguration).length > 0;
  };

  const hasSystemInfoData = () => {
    return Object.keys(systemInfo).length > 0;
  };

  const hasCombinersData = () => {
    return Object.keys(combiners).length > 0;
  };

  const hasAuDrawerData = () => {
    return (
      hasCombinersData() ||
      hasSystemInfoData() ||
      hasBandConfigurationData() ||
      Object.keys(lanConnectivity).length > 0 ||
      alarms.length > 0
    );
  };

  // Helper function to get alarm status color
  const getAlarmStatusColor = (alarms: any[], isDisabled: boolean) => {
    if (isDisabled || !alarms || alarms.length === 0) {
      return '#9e9e9e'; // Gray
    }

    // Check if all alarms are disabled/not configured
    // An alarm is disabled if:
    // 1. Missing alarmStat field (not configured), OR
    // 2. Has alarmStat but severity is 0 (disabled/not important)
    const allAlarmsDisabled = alarms.every(alarm => {
      const hasNoStat = alarm.alarmStat === undefined || alarm.alarmStat === null;
      const severity = parseInt(alarm.alarmSeverity || '0');
      const isZeroSeverity = severity === 0;
      
      // Disabled if no stat field OR if stat exists but severity is 0
      return hasNoStat || (alarm.alarmStat !== undefined && isZeroSeverity);
    });
    
    if (allAlarmsDisabled) {
      return '#9e9e9e'; // Gray - all alarms disabled/not configured
    }

    let hasActiveAlarm = false;
    let mostCriticalSeverity = 0;

    for (const alarm of alarms) {
      const stat = alarm.alarmStat;
      const severity = parseInt(alarm.alarmSeverity || '0');
      
      // Check if alarm is active (stat = 1 means active)
      if (stat === '1' || stat === 1) {
        hasActiveAlarm = true;
        if (severity > mostCriticalSeverity) {
          mostCriticalSeverity = severity;
        }
      }
    }

    if (!hasActiveAlarm) {
      return '#4caf50'; // Green - clear/no active alarms
    }

    // Map severity to colors (TR-069 standard severity levels)
    // 0: Cleared, 1: Indeterminate, 2: Warning, 3: Minor, 4: Major, 5: Critical
    if (mostCriticalSeverity >= 4) {
      return '#f44336'; // Red - Major/Critical
    } else if (mostCriticalSeverity >= 2) {
      return '#ff9800'; // Orange - Warning/Minor
    } else {
      return '#4caf50'; // Green - Indeterminate or cleared
    }
  };

  // Calculate module status based on all module and port alarms
  const getModuleStatus = (moduleNumber: number) => {
    const moduleAlarms = alarms.filter((alarm: any) => {
      const name = alarm.name || '';
      return name.includes(`radioModule${moduleNumber}`);
    });
    return getAlarmStatusColor(moduleAlarms, isDeviceDisabled);
  };

  // Calculate port status based on port-specific alarms
  const getPortStatus = (moduleNumber: number, portNumber: number) => {
    const portAlarms = alarms.filter((alarm: any) => {
      const name = alarm.name || '';
      return name.includes(`radioModule${moduleNumber}Port${portNumber}`);
    });
    return getAlarmStatusColor(portAlarms, isDeviceDisabled);
  };

  // Calculate optical port status based on transceiver failure alarms
  const getOpticalPortStatus = (portNumber: number) => {
    const opticalAlarms = alarms.filter((alarm: any) => {
      const name = alarm.name || '';
      return name === `op${portNumber}TransceiverFailureAlarm`;
    });
    return getAlarmStatusColor(opticalAlarms, isDeviceDisabled);
  };

  // Decode colon-separated hex label (e.g. "6e:6f:74:...") to a readable string, stripping null bytes
  const decodeHexLabel = (raw: string): string => {
    if (!raw || !raw.includes(':')) return raw;
    return raw
      .split(':')
      .map((h) => parseInt(h, 16))
      .filter((n) => n > 0)
      .map((n) => String.fromCharCode(n))
      .join('');
  };

  // Get port label from combiners data, decoded from hex
  const getPortLabel = (moduleNumber: number, portNumber: number): string => {
    const labelKey = `radioModule${moduleNumber}Port${portNumber}Label`;
    const raw = combiners[labelKey];
    if (!raw) return `P${portNumber}`;
    const decoded = decodeHexLabel(String(raw));
    if (!decoded || /^not\s*avail/i.test(decoded)) return 'N/A';
    return decoded;
  };

  // Get port attenuation value
  const getPortAttenuation = (moduleNumber: number, portNumber: number) => {
    const attKey = `radioModule${moduleNumber}Port${portNumber}Attenuation`;
    const value = combiners[attKey];
    if (value === undefined || value === null || value === '65535') return 'N/A';
    return `${value} dB`;
  };

  // Get port power distribution value
  const getPortPowerDistribution = (moduleNumber: number, portNumber: number) => {
    const powerKey = `radioModule${moduleNumber}Port${portNumber}PowerDistribution`;
    const value = combiners[powerKey];
    if (value === undefined || value === null) return 'N/A';
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 100) return 'N/A';
    return `${value}%`;
  };

  // Get port input power value (divided by 1000 for dBm)
  const getPortInputPower = (moduleNumber: number, portNumber: number) => {
    const inputKey = `radioModule${moduleNumber}Port${portNumber}InputPower`;
    const value = combiners[inputKey];
    if (value === undefined || value === null || value === '') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'N/A';
    return `${(numValue / 1000).toFixed(2)} dBm`;
  };

  // Get port output power value (divided by 1000 for dBm)
  const getPortOutputPower = (moduleNumber: number, portNumber: number) => {
    const outputKey = `radioModule${moduleNumber}Port${portNumber}OutputPower`;
    const value = combiners[outputKey];
    if (value === undefined || value === null || value === '') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'N/A';
    return `${(numValue / 1000).toFixed(2)} dBm`;
  };

  // Get optical port RX power (divided by 100 for dBm)
  const getOpticalPortRxPower = (portNumber: number) => {
    const rxKey = `op${portNumber}TransceiverRXpower`;
    const value = lanConnectivity[rxKey];
    if (value === undefined || value === null || value === '') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue === 0) return 'N/A';
    return `${(numValue / 100).toFixed(2)} dBm`;
  };

  // Get optical port TX power (divided by 100 for dBm)
  const getOpticalPortTxPower = (portNumber: number) => {
    const txKey = `op${portNumber}TransceiverTXpower`;
    const value = lanConnectivity[txKey];
    if (value === undefined || value === null || value === '') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue === 0) return 'N/A';
    return `${(numValue / 100).toFixed(2)} dBm`;
  };

  // Format TDD switch/status values (1 = On, 0 = Off)
  const formatOnOff = (value: any): string => {
    if (value === undefined || value === null) return "N/A";
    const val = String(value);
    return val === "1" ? "On" : val === "0" ? "Off" : val;
  };

  // Get module RF signal active status
  const getModuleRfSignalActive = (moduleNum: number) => {
    const key = `radioModule${moduleNum}RfSignalActive`;
    return formatOnOff(systemInfo[key]);
  };

  // Get module power switch status
  const getModulePowerSwitch = (moduleNum: number) => {
    const key = `radioModule${moduleNum}PowerSwitch`;
    return formatOnOff(combiners[key]);
  };

  // Get module DL attenuation
  const getModuleDlAttenuation = (moduleNum: number) => {
    const key = `radioModule${moduleNum}DlAttenuation`;
    const value = systemInfo[key];
    if (value === undefined || value === null) return 'N/A';
    return `${value} dB`;
  };

  // Get module UL attenuation
  const getModuleUlAttenuation = (moduleNum: number) => {
    const key = `radioModule${moduleNum}UlAttenuation`;
    const value = systemInfo[key];
    if (value === undefined || value === null) return 'N/A';
    return `${value} dB`;
  };

  // Get module DL centre frequency
  const getModuleDlCentreFrequency = (moduleNum: number) => {
    const key = `radioModule${moduleNum}DlCentreFrequency`;
    const value = systemInfo[key];
    if (value === undefined || value === null || value === '0' || value === 0) return 'N/A';
    return `${(parseInt(value) / 1000).toFixed(0)} MHz`;
  };

  // Get module UL centre frequency
  const getModuleUlCentreFrequency = (moduleNum: number) => {
    const key = `radioModule${moduleNum}UlCentreFrequency`;
    const value = systemInfo[key];
    if (value === undefined || value === null || value === '0' || value === 0) return 'N/A';
    return `${(parseInt(value) / 1000).toFixed(0)} MHz`;
  };

  // Get module DL frequency range
  const getModuleDlFrequencyRange = (moduleNum: number) => {
    const startKey = `radioModule${moduleNum}DlFrequencyStart`;
    const endKey = `radioModule${moduleNum}DlFrequencyEnd`;
    const start = bandConfiguration[startKey];
    const end = bandConfiguration[endKey];
    if (!start || !end || start === '0' || end === '0') return 'N/A';
    return `${(parseInt(start) / 1000).toFixed(0)}-${(parseInt(end) / 1000).toFixed(0)} MHz`;
  };

  // Get module UL frequency range
  const getModuleUlFrequencyRange = (moduleNum: number) => {
    const startKey = `radioModule${moduleNum}UlFrequencyStart`;
    const endKey = `radioModule${moduleNum}UlFrequencyEnd`;
    const start = bandConfiguration[startKey];
    const end = bandConfiguration[endKey];
    if (!start || !end || start === '0' || end === '0') return 'N/A';
    return `${(parseInt(start) / 1000).toFixed(0)}-${(parseInt(end) / 1000).toFixed(0)} MHz`;
  };

  // Format attenuation values (divide by 2, add " dB")
  const formatAttenuation = (value: any): string => {
    if (value === undefined || value === null || value === "") return "N/A";
    const numValue = parseFloat(String(value));
    if (isNaN(numValue)) return "N/A";
    return `${(numValue / 2).toFixed(1)} dB`;
  };

  // Format frequency/bandwidth values (divide by 1000, add " MHz")
  const formatFrequency = (value: any): string => {
    if (value === undefined || value === null || value === "" || value === "0") return "N/A";
    const numValue = parseFloat(String(value));
    if (isNaN(numValue)) return "N/A";
    return `${(numValue / 1000).toFixed(1)} MHz`;
  };

  // Format power distribution as percentage
  const formatPercentage = (value: any): string => {
    if (value === undefined || value === null || value === "") return "N/A";
    const numValue = parseFloat(String(value));
    if (isNaN(numValue)) return "N/A";
    return `${numValue}%`;
  };

  // Format frequency range (Start - End)
  const formatFrequencyRange = (startValue: any, endValue: any): string => {
    if (startValue === undefined || startValue === null || startValue === "" || startValue === "0") {
      if (endValue === undefined || endValue === null || endValue === "" || endValue === "0") {
        return "N/A";
      }
    }
    const start = parseFloat(String(startValue));
    const end = parseFloat(String(endValue));
    if (isNaN(start) || isNaN(end)) return "N/A";
    return `${(start / 1000).toFixed(1)} - ${(end / 1000).toFixed(1)} MHz`;
  };

  // Format attenuation control mode
  const formatAttenuationControlMode = (value: any): string => {
    console.log("formatAttenuationControlMode called with:", value, "type:", typeof value);
    if (value === undefined || value === null || value === "") return "N/A";
    const val = String(value);
    console.log("formatAttenuationControlMode converted to string:", val);
    switch (val) {
      case "0":
        return "Manual";
      case "1":
        return "Automatic";
      default:
        return val;
    }
  };

  // Format automatic level interval
  const formatAutomaticLevelInterval = (value: any): string => {
    console.log("formatAutomaticLevelInterval called with:", value, "type:", typeof value);
    if (value === undefined || value === null) return "N/A";
    const val = String(value);
    console.log("formatAutomaticLevelInterval converted to string:", val);
    switch (val) {
      case "0":
        return "Test 60 seconds";
      case "1":
        return "6 hours";
      case "2":
        return "12 hours";
      case "3":
        return "24 hours";
      default:
        return val;
    }
  };

  // Format transmission allocation in fiber core
  const formatTransmissionAllocation = (value: any): string => {
    if (value === undefined || value === null) return "N/A";
    const val = String(value);
    switch (val) {
      case "0":
        return "None";
      case "1":
        return "Fiber Core A";
      case "2":
        return "Fiber Core B";
      default:
        return val;
    }
  };

  // Format info check/clock status
  const formatInfoCheck = (value: any): string => {
    if (value === undefined || value === null) return "N/A";
    const val = String(value);
    switch (val) {
      case "0":
        return "Valid";
      case "1":
        return "Invalid";
      default:
        return val;
    }
  };

  // Get module serial number
  const getModuleSerialNumber = (moduleNum: number) => {
    // Handle Module 4 with lowercase 'm' (typo in data source)
    if (moduleNum === 4) {
      const value = combiners.radiomodule4SerialNumber|| systemInfo.radiomodule4SerialNumber;
      return value?.trim() || 'N/A';
    }
    const key = `radioModule${moduleNum}SerialNumber`;
    const value = combiners[key] || systemInfo[key];
    return value?.trim() || 'N/A';
  };

  // Get module BW occupied in fiber
  const getModuleBwOccupiedInFiber = (moduleNum: number) => {
    const key = `radioModule${moduleNum}BwOccupiedInFiber`;
    const value = combiners[key] || systemInfo[key] || bandConfiguration[`radioModule${moduleNum}SignalTransBandwidthOccupied`];
    if (value === undefined || value === null || value === '0' || value === 0) return 'N/A';
    const numValue = parseFloat(String(value));
    if (isNaN(numValue)) return 'N/A';
    return `${(numValue / 1000).toFixed(1)} MHz`;
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Main Content */}
      <Box sx={{ 
        width: '100%',
        overflowX: 'auto',
        backgroundColor: 'var(--background)',
      }}>
        <Box sx={{
          minWidth: '1400px',
          p: 3
        }}>
        {/* RF Topology Content */}
        {hasAuDrawerData() && (
          <Box>
            {/* Summary Card */}
            <Card className="mb-3">
              <CardContent className="px-4 py-4">
                <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">General Information</p>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Model Number</p>
                    <p className="text-sm text-foreground">{parsedAttributes.devModelNumber || device.model || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Serial Number</p>
                    <p className="text-sm text-foreground">{parsedAttributes.devSerialNumber || device.serialNumber || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Software Version</p>
                    <p className="text-sm text-foreground">{parsedAttributes.softwareVersion || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Temperature</p>
                    <p className="text-sm text-foreground">{systemInfo.elementOperatingTemperature ? `${systemInfo.elementOperatingTemperature}°C` : "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Location</p>
                    <p className="text-sm text-foreground">{device.location || parsedAttributes.location || parsedAttributes.devSiteName || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Last Update</p>
                    <p className="text-sm text-foreground">{device.lastSeen || device.updatedAt || "N/A"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Optical Ports Display */}
            <Card className="mb-4">
              <CardContent className="px-4 py-4">
                <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">Optical Ports</p>
                <Box sx={{
                  display: 'flex', 
                  gap: 1.5,
                  justifyContent: 'center',
                  pb: 1,
                  flexWrap: 'nowrap'
                }}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((portNum) => (
                  <Box
                    key={portNum}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.5,
                      minWidth: 90
                    }}
                  >
                    {/* Power readings above port box */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-end',
                      width: '100%',
                      mb: 0.5,
                      px: 0.5,
                      gap: 2
                    }}>
                      <Box sx={{ 
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                      }}>
                        <Typography variant="caption" sx={{ 
                          display: 'block',
                          fontSize: '0.55rem',
                          color: getOpticalPortRxPower(portNum) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                          fontWeight: 600,
                          mb: 0.25
                        }}>
                          {getOpticalPortRxPower(portNum)}
                        </Typography>
                        <Typography sx={{ 
                          fontSize: '0.9rem',
                          color: 'var(--primary)',
                          fontWeight: 700,
                          lineHeight: 1
                        }}>
                          ↓
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                      }}>
                        <Typography variant="caption" sx={{ 
                          display: 'block',
                          fontSize: '0.55rem',
                          color: getOpticalPortTxPower(portNum) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                          fontWeight: 600,
                          mb: 0.25
                        }}>
                          {getOpticalPortTxPower(portNum)}
                        </Typography>
                        <Typography sx={{ 
                          fontSize: '0.9rem',
                          color: 'var(--primary)',
                          fontWeight: 700,
                          lineHeight: 1
                        }}>
                          ↑
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Port box */}
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%',
                      p: 2,
                      border: '3px solid var(--border)',
                      borderRadius: 2,
                      backgroundColor: "var(--card) !important",
                      boxShadow: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                        borderColor: 'var(--primary)',
                        cursor: 'pointer'
                      }
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 700,
                      color: 'var(--foreground)',
                      fontSize: '0.875rem'
                    }}>
                      OP {portNum}
                    </Typography>
                    <Box sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: getOpticalPortStatus(portNum),
                      border: '3px solid var(--border)',
                      transition: 'all 0.2s ease'
                    }} />
                  </Box>
                  </Box>
                ))}
                </Box>
              </CardContent>
            </Card>
            
            {/* Modules Display */}
            <div className="mb-6">
            <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-4">
              Radio Modules
            </p>
            <Grid container spacing={4} sx={{ flexWrap: 'nowrap' }}>
              {[1, 2, 3, 4].map((moduleNum) => (
                <Grid item sx={{ minWidth: '320px', flex: 1 }} key={moduleNum}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {/* Module Card */}
                    <Card 
                      onClick={() => handleModuleClick(moduleNum)}
                      className="w-full cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:shadow-md hover:border-primary">
                      <CardContent className="py-3 px-4">
                        {/* Module Header - Left aligned */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 1,
                          pb: 1,
                          borderBottom: '2px solid var(--border)',
                          flexWrap: 'nowrap',
                          gap: 0
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--foreground)', whiteSpace: 'nowrap' }}>
                              Module {moduleNum}
                            </Typography>
                            <Box sx={{
                              width: 14,
                              height: 14,
                              borderRadius: '50%',
                              backgroundColor: getModuleStatus(moduleNum),
                              border: '2px solid var(--border)',

                            }} />
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'nowrap' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'var(--muted-foreground)', whiteSpace: 'nowrap' }}>
                              RF Signal: <strong>{getModuleRfSignalActive(moduleNum)}</strong>
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'var(--muted-foreground)', whiteSpace: 'nowrap' }}>
                              Power: <strong>{getModulePowerSwitch(moduleNum)}</strong>
                            </Typography>
                          </Box>
                        </Box>

                        {/* DL and UL Split Layout */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {/* DL Column */}
                          <Box sx={{ flex: 1, borderRight: '1px solid var(--border)', pr: 1 }}>
                            <p className="text-xs font-bold text-chart-3 mt-0 mb-1">↓ DL</p>
                            <p className={`text-[0.6rem] mt-0 mb-0.5 ${getModuleDlAttenuation(moduleNum) !== 'N/A' ? 'text-success' : 'text-muted-foreground'}`}>
                              Attenuation: <strong>{getModuleDlAttenuation(moduleNum)}</strong>
                            </p>
                            <p className={`text-[0.6rem] mt-0 mb-0.5 ${getModuleDlCentreFrequency(moduleNum) !== 'N/A' ? 'text-success' : 'text-muted-foreground'}`}>
                              Centre Freq: <strong>{getModuleDlCentreFrequency(moduleNum)}</strong>
                            </p>
                            <p className={`text-[0.6rem] mt-0 ${getModuleDlFrequencyRange(moduleNum) !== 'N/A' ? 'text-success' : 'text-muted-foreground'}`}>
                              Range: <strong>{getModuleDlFrequencyRange(moduleNum)}</strong>
                            </p>
                          </Box>

                          {/* UL Column */}
                          <Box sx={{ flex: 1, pl: 1 }}>
                            <p className="text-xs font-bold text-chart-3 mt-0 mb-1">↑ UL</p>
                            <p className={`text-[0.6rem] mt-0 mb-0.5 ${getModuleUlAttenuation(moduleNum) !== 'N/A' ? 'text-success' : 'text-muted-foreground'}`}>
                              Attenuation: <strong>{getModuleUlAttenuation(moduleNum)}</strong>
                            </p>
                            <p className={`text-[0.6rem] mt-0 mb-0.5 ${getModuleUlCentreFrequency(moduleNum) !== 'N/A' ? 'text-success' : 'text-muted-foreground'}`}>
                              Centre Freq: <strong>{getModuleUlCentreFrequency(moduleNum)}</strong>
                            </p>
                            <p className={`text-[0.6rem] mt-0 ${getModuleUlFrequencyRange(moduleNum) !== 'N/A' ? 'text-success' : 'text-muted-foreground'}`}>
                              Range: <strong>{getModuleUlFrequencyRange(moduleNum)}</strong>
                            </p>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                    
                    {/* Ports as "legs" below module */}
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 1, 
                      width: '100%',
                      justifyContent: 'space-between',
                      px: 0.5,
                      mt: 0,
                      pb: 1
                    }}>
                      {[1, 2, 3, 4].map((portNum) => (
                        <Box 
                          key={portNum}
                          sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                          }}
                        >
                          {/* Leg connector line */}
                          <Box sx={{
                            width: '80%',
                            height: 30,
                            borderLeft: '3px solid var(--border)',
                            borderRight: '3px solid var(--border)',
                            backgroundColor: 'transparent'
                          }} />
                          
                          {/* Port box */}
                          <Box sx={{
                            width: '100%',
                            border: '3px solid var(--border)',
                            borderRadius: 1,
                            p: 1.5,
                            textAlign: 'center',
                            backgroundColor: "var(--card) !important",
                            boxShadow: 1,
                            '&:hover': {
                              backgroundColor: 'var(--hover)',
                              cursor: 'pointer',
                              borderColor: 'var(--primary)',
                              boxShadow: 2
                            }
                          }}>
                            <Tooltip title={getPortLabel(moduleNum, portNum)} placement="top">
                            <Typography variant="caption" sx={{ 
                              fontWeight: 700,
                              display: 'block',
                              mb: 0.5,
                              color: 'var(--foreground)',
                              fontSize: '0.75rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%',
                            }}>
                              {getPortLabel(moduleNum, portNum).slice(0, 6)}
                            </Typography>
                            </Tooltip>
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              mb: 0.5
                            }}>
                              <Box sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: getPortStatus(moduleNum, portNum),
                                border: '2px solid var(--border)',

                              }} />
                            </Box>
                            <Box sx={{ 
                              mt: 0.5,
                              pt: 0.5,
                              borderTop: '1px solid var(--border)'
                            }}>
                              <Typography variant="caption" sx={{ 
                                display: 'block',
                                fontSize: '0.65rem',
                                color: getPortAttenuation(moduleNum, portNum) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                                fontWeight: 500
                              }}>
                                Att: {getPortAttenuation(moduleNum, portNum)}
                              </Typography>
                              <Typography variant="caption" sx={{ 
                                display: 'block',
                                fontSize: '0.65rem',
                                color: getPortPowerDistribution(moduleNum, portNum) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                                fontWeight: 500
                              }}>
                                Pwr: {getPortPowerDistribution(moduleNum, portNum)}
                              </Typography>
                            </Box>
                          </Box>
                          
                          {/* Power readings below port box */}
                          <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            width: '100%',
                            mt: 0,
                            px: 0.5
                          }}>
                            <Box sx={{ 
                              textAlign: 'center',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center'
                            }}>
                              <Typography sx={{ 
                                fontSize: '1rem',
                                color: 'var(--primary)',
                                fontWeight: 700,
                                lineHeight: 1
                              }}>
                                ↑
                              </Typography>
                              <Typography variant="caption" sx={{ 
                                display: 'block',
                                fontSize: '0.6rem',
                                color: getPortInputPower(moduleNum, portNum) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                                fontWeight: 600,
                                mt: 0.25
                              }}>
                                {getPortInputPower(moduleNum, portNum)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
            </div>
            
            {/* General Alarms and Settings Section - Side by Side */}
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* General Alarms Card */}
              <Grid item xs={6}>
                <Card className="h-full">
                  <CardContent className="px-4 py-4">
                    <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">General Alarms</p>
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5
                    }}>
                      {/* Movement Alarm */}
                      {(() => {
                        const alarm = alarms.find(a => a.name === 'movementAlarm');
                        const statusColor = alarm ? getAlarmStatusColor([alarm], isDeviceDisabled) : '#9e9e9e';
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: statusColor,
                                border: '2px solid var(--border)',
                              }}
                            />
                            <p className="text-sm text-foreground">Movement Alarm</p>
                          </Box>
                        );
                      })()}
                       {/* External 1 Alarm */}
                      {(() => {
                        const alarm = alarms.find(a => a.name === 'external1Alarm');
                        const statusColor = alarm ? getAlarmStatusColor([alarm], isDeviceDisabled) : '#9e9e9e';
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: statusColor,
                                border: '2px solid var(--border)',
                              }}
                            />
                            <p className="text-sm text-foreground">External 1 Alarm</p>
                          </Box>
                        );
                      })()}
                        {/* External 2 Alarm */}
                      {(() => {
                        const alarm = alarms.find(a => a.name === 'external2Alarm');
                        const statusColor = alarm ? getAlarmStatusColor([alarm], isDeviceDisabled) : '#9e9e9e';
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: statusColor,
                                border: '2px solid var(--border)',
                              }}
                            />
                            <p className="text-sm text-foreground">External 2 Alarm</p>
                          </Box>
                        );
                      })()}
                        {/* External 3 Alarm */}
                      {(() => {
                        const alarm = alarms.find(a => a.name === 'external3Alarm');
                        const statusColor = alarm ? getAlarmStatusColor([alarm], isDeviceDisabled) : '#9e9e9e';
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: statusColor,
                                border: '2px solid var(--border)',
                              }}
                            />
                            <p className="text-sm text-foreground">External 3 Alarm</p>
                          </Box>
                        );
                      })()}
                        {/* External 4 Alarm */}
                      {(() => {
                        const alarm = alarms.find(a => a.name === 'external4Alarm');
                        const statusColor = alarm ? getAlarmStatusColor([alarm], isDeviceDisabled) : '#9e9e9e';
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: statusColor,
                                border: '2px solid var(--border)',
                              }}
                            />
                            <p className="text-sm text-foreground">External 4 Alarm</p>
                          </Box>
                        );
                      })()}
                      {/* Open Case Alarm */}
                      {(() => {
                        const alarm = alarms.find(a => a.name === 'openCaseAlarm');
                        const statusColor = alarm ? getAlarmStatusColor([alarm], isDeviceDisabled) : '#9e9e9e';
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: statusColor,
                                border: '2px solid var(--border)',
                              }}
                            />
                            <p className="text-sm text-foreground">Open Case Alarm</p>
                          </Box>
                        );
                      })()}
                      
                      {/* Over Temperature Alarm */}
                      {(() => {
                        const alarm = alarms.find(a => a.name === 'overtemperatureAlarm');
                        const statusColor = alarm ? getAlarmStatusColor([alarm], isDeviceDisabled) : '#9e9e9e';
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: statusColor,
                                border: '2px solid var(--border)',
                              }}
                            />
                            <p className="text-sm text-foreground">Over Temperature Alarm</p>
                          </Box>
                        );
                      })()}
                      
                      {/* Band Configuration Failure Alarm */}
                      {(() => {
                        const alarm = alarms.find(a => a.name === 'bandConfigurationFailureAlarm');
                        const statusColor = alarm ? getAlarmStatusColor([alarm], isDeviceDisabled) : '#9e9e9e';
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: statusColor,
                                border: '2px solid var(--border)',
                              }}
                            />
                            <p className="text-sm text-foreground">Band Configuration Failure Alarm</p>
                          </Box>
                        );
                      })()}
                      
                      {/* RF Link Alarm */}
                      {(() => {
                        const alarm = alarms.find(a => a.name === 'rfLinkAlarm');
                        const statusColor = alarm ? getAlarmStatusColor([alarm], isDeviceDisabled) : '#9e9e9e';
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: statusColor,
                                border: '2px solid var(--border)',
                              }}
                            />
                            <p className="text-sm text-foreground">RF Link Alarm</p>
                          </Box>
                        );
                      })()}
                      
                      {/* NTP Alarm */}
                      {(() => {
                        const alarm = alarms.find(a => a.name === 'ntpAlarm');
                        const statusColor = alarm ? getAlarmStatusColor([alarm], isDeviceDisabled) : '#9e9e9e';
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: statusColor,
                                border: '2px solid var(--border)',
                              }}
                            />
                            <p className="text-sm text-foreground">NTP Alarm</p>
                          </Box>
                        );
                      })()}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* General Settings Card */}
              <Grid item xs={6}>
                <Card className="h-full">
                  <CardContent className="px-4 py-4">
                    <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">General Settings</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1">Attenuation Control Mode</p>
                        <p className="text-sm text-foreground">{formatAttenuationControlMode(combiners.attenuationControlMode || systemInfo.attenuationControlMode || parsedAttributes.attenuationControlMode)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1">Automatic Level Interval</p>
                        <p className="text-sm text-foreground">{formatAutomaticLevelInterval(combiners.automaticLevelInterval || systemInfo.automaticLevelInterval|| parsedAttributes.automaticLevelInterval)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1">Baseband Bandwidth</p>
                        <p className="text-sm text-foreground">{formatFrequency(bandConfiguration.systemSignalBandwidth)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1">Transmission Bandwidth</p>
                        <p className="text-sm text-foreground">{formatFrequency(bandConfiguration.systemTransmissionBandwidth)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1">System Signal Bandwidth</p>
                        <p className="text-sm text-foreground">{formatFrequency(bandConfiguration.systemSignalBandwidth)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1">System Transmission Bandwidth</p>
                        <p className="text-sm text-foreground">{formatFrequency(bandConfiguration.systemTransmissionBandwidth)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1">System Bandwidth Occupied in Fiber</p>
                        <p className="text-sm text-foreground">{formatFrequency(bandConfiguration.systemBandwidthOccupiedinFiber)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1">800 Mode On/OFF</p>
                        <p className="text-sm text-foreground">{formatOnOff(bandConfiguration.mode800MEnable)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {!hasAuDrawerData() && (
          <Card className="mb-3">
            <CardContent className="px-4 py-4">
              <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">General Information</p>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Model Number</p>
                  <p className="text-sm text-foreground">{parsedAttributes.devModelNumber || device.model || "N/A"}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Serial Number</p>
                  <p className="text-sm text-foreground">{parsedAttributes.devSerialNumber || device.serialNumber || "N/A"}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Software Version</p>
                  <p className="text-sm text-foreground">{parsedAttributes.softwareVersion || "N/A"}</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3 mb-0">
                Detailed AU telemetry is not available in this payload yet.
              </p>
            </CardContent>
          </Card>
        )}
        </Box>
      </Box>
      
      {/* Module Detail Dialog */}
      <Dialog 
        open={moduleDialogOpen} 
        onClose={handleCloseModuleDialog}
        maxWidth="lg"
        fullWidth
        sx={dialogLayerSx}
        PaperProps={{
          sx: {
            backgroundColor: "var(--card) !important",
            border: '1px solid var(--border)',
            borderRadius: '12px',
            minHeight: '80vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '2px solid var(--border)',
          pb: 2,
          px: 4
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--foreground)' }}>
            Module {selectedModule} Details
          </Typography>
          <IconButton onClick={handleCloseModuleDialog} size="small" sx={{ color: 'var(--foreground)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, px: 4, pb: 4 }}>
          {selectedModule && (
            <Box sx={{ maxWidth: '900px', mx: 'auto', mt: 3 }}>
              {/* Module Card - Smaller Version (75% size) */}
              <Card >
                <CardContent className="py-3 px-4">
                  {/* Module Header */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mb: 1.15,
                    pb: 1.15,
                    borderBottom: '1px solid var(--border)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.15 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--foreground)', fontSize: '0.9rem' }}>
                        Module {selectedModule}
                      </Typography>
                      <Box sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: getModuleStatus(selectedModule),
                        border: '1.5px solid var(--border)',

                      }} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: 'var(--muted-foreground)', fontSize: '0.65rem' }}>
                        RF Signal: <strong>{getModuleRfSignalActive(selectedModule)}</strong>
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'var(--muted-foreground)', fontSize: '0.65rem' }}>
                        Power: <strong>{getModulePowerSwitch(selectedModule)}</strong>
                      </Typography>
                    </Box>
                  </Box>

                  {/* Module Info Section */}
                  <Box sx={{ 
                    mb: 1.5, 
                    pb: 1.5, 
                    borderBottom: '1px solid var(--border)',
                    backgroundColor: "var(--card) !important",
                    p: 1.15,
                    borderRadius: 0.75
                  }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: 'var(--muted-foreground)', display: 'block', mb: 0.2, fontSize: '0.55rem' }}>
                          Module Serial Number
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          color: getModuleSerialNumber(selectedModule) !== 'N/A' ? 'var(--foreground)' : 'var(--muted-foreground)'
                        }}>
                          {getModuleSerialNumber(selectedModule)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: 'var(--muted-foreground)', display: 'block', mb: 0.2, fontSize: '0.55rem' }}>
                          BW Occupied in Fiber
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          color: getModuleBwOccupiedInFiber(selectedModule) !== 'N/A' ? 'var(--foreground)' : 'var(--muted-foreground)'
                        }}>
                          {getModuleBwOccupiedInFiber(selectedModule)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* DL and UL Split Layout */}
                  <Grid container spacing={1.5}>
                    {/* DL Column */}
                    <Grid item xs={6}>
                      <Box sx={{ 
                        p: 1.15, 
                        borderRight: '1px solid var(--border)',
                        backgroundColor: "var(--card) !important",
                        borderRadius: 0.75
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.15 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--chart-3)', fontSize: '0.7rem' }}>
                            ↓ Downlink (DL)
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.95 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'var(--muted-foreground)', display: 'block', mb: 0.2, fontSize: '0.55rem' }}>
                              Attenuation
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              color: getModuleDlAttenuation(selectedModule) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)'
                            }}>
                              {getModuleDlAttenuation(selectedModule)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'var(--muted-foreground)', display: 'block', mb: 0.2, fontSize: '0.55rem' }}>
                              Centre Frequency
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              color: getModuleDlCentreFrequency(selectedModule) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)'
                            }}>
                              {getModuleDlCentreFrequency(selectedModule)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'var(--muted-foreground)', display: 'block', mb: 0.2, fontSize: '0.55rem' }}>
                              Frequency Range
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              color: getModuleDlFrequencyRange(selectedModule) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)'
                            }}>
                              {getModuleDlFrequencyRange(selectedModule)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>

                    {/* UL Column */}
                    <Grid item xs={6}>
                      <Box sx={{ 
                        p: 1.15,
                        backgroundColor: "var(--card) !important",
                        borderRadius: 0.75
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.15 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--chart-3)', fontSize: '0.7rem' }}>
                            ↑ Uplink (UL)
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.95 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'var(--muted-foreground)', display: 'block', mb: 0.2, fontSize: '0.55rem' }}>
                              Attenuation
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              color: getModuleUlAttenuation(selectedModule) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)'
                            }}>
                              {getModuleUlAttenuation(selectedModule)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'var(--muted-foreground)', display: 'block', mb: 0.2, fontSize: '0.55rem' }}>
                              Centre Frequency
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              color: getModuleUlCentreFrequency(selectedModule) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)'
                            }}>
                              {getModuleUlCentreFrequency(selectedModule)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'var(--muted-foreground)', display: 'block', mb: 0.2, fontSize: '0.55rem' }}>
                              Frequency Range
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              color: getModuleUlFrequencyRange(selectedModule) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)'
                            }}>
                              {getModuleUlFrequencyRange(selectedModule)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* RF Ports */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  justifyContent: 'space-between'
                }}>
                  {[1, 2, 3, 4].map((portNum) => (
                    <Box 
                      key={portNum}
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                      }}
                    >
                      {/* Leg connector line */}
                      <Box sx={{
                        width: '80%',
                        height: 30,
                        borderLeft: '3px solid var(--border)',
                        borderRight: '3px solid var(--border)',
                        backgroundColor: 'transparent'
                      }} />
                      
                      {/* Port box */}
                      <Box sx={{
                        width: '100%',
                        border: '3px solid var(--border)',
                        borderRadius: 1,
                        p: 1.5,
                        textAlign: 'center',
                        backgroundColor: "var(--card) !important",
                        boxShadow: 1,
                        '&:hover': {
                          backgroundColor: 'var(--hover)',
                          cursor: 'pointer',
                          borderColor: 'var(--primary)',
                          boxShadow: 4
                        }
                      }}>
                        <Tooltip title={getPortLabel(selectedModule, portNum)} placement="top">
                        <Typography variant="body2" sx={{ 
                          fontWeight: 700,
                          display: 'block',
                          mb: 0.75,
                          color: 'var(--foreground)',
                          fontSize: '0.875rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%',
                        }}>
                          {getPortLabel(selectedModule, portNum).slice(0, 6)}
                        </Typography>
                        </Tooltip>
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          mb: 0.75
                        }}>
                          <Box sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: getPortStatus(selectedModule, portNum),
                            border: '2px solid var(--border)'
                          }} />
                        </Box>
                        <Box sx={{ 
                          mt: 0.75,
                          pt: 0.75,
                          borderTop: '1px solid var(--border)'
                        }}>
                          <Typography variant="caption" sx={{ 
                            display: 'block',
                            fontSize: '0.75rem',
                            color: getPortAttenuation(selectedModule, portNum) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                            fontWeight: 600,
                            mb: 0.25
                          }}>
                            Att: {getPortAttenuation(selectedModule, portNum)}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            display: 'block',
                            fontSize: '0.75rem',
                            color: getPortPowerDistribution(selectedModule, portNum) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                            fontWeight: 600
                          }}>
                            Pwr: {getPortPowerDistribution(selectedModule, portNum)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Power readings below port box */}
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        width: '100%',
                        mt: 0.75,
                        px: 0.5
                      }}>
                        <Box sx={{ 
                          textAlign: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center'
                        }}>
                          <Typography sx={{ 
                            fontSize: '1.25rem',
                            color: 'var(--primary)',
                            fontWeight: 700,
                            lineHeight: 1
                          }}>
                            ↑
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            display: 'block',
                            fontSize: '0.75rem',
                            color: getPortInputPower(selectedModule, portNum) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                            fontWeight: 600,
                            mt: 0.25
                          }}>
                            {getPortInputPower(selectedModule, portNum)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Input Power Chart */}
              {moduleChartConfig && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ width: '100%' }}>
                    {/* Input Power Chart - All 4 Ports */}
                    <Box sx={{ minHeight: 300 }}>
                      <DasPowerDetectorChart
                        config={{
                          ...moduleChartConfig.dlConfig,
                          title: 'Input Power (Ports 1-4)',
                        }}
                        onFullscreenToggle={handleOpenFullscreen}
                        isFullscreen={false}
                        additionalParams={moduleChartConfig.dlChartParams}
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Module Alarms */}
              <Card className="mt-2">
                <CardContent className="px-4 py-4">
                  <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">
                    Module Alarms Comparison
                  </p>
                  <MA600G1RadioModuleAlarmTable 
                    alarms={alarms}
                    isDeviceDisabled={isDeviceDisabled}
                    selectedModule={selectedModule}
                  />
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Input Power Fullscreen Dialog */}
      <Dialog
        open={fullscreenChart === 'inputPower'}
        onClose={handleCloseFullscreen}
        maxWidth="xl"
        fullWidth
        sx={dialogLayerSx}
        PaperProps={{
          sx: {
            backgroundColor: "var(--card) !important",
            border: '1px solid var(--border)',
            borderRadius: '12px',
            minHeight: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '2px solid var(--border)',
          pb: 2,
          px: 4
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--foreground)' }}>
            Input Power (Ports 1-4)
          </Typography>
          <IconButton onClick={handleCloseFullscreen} size="small" sx={{ color: 'var(--foreground)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 3, pb: 3 }}>
          {fullscreenChart === 'inputPower' && moduleChartConfig && (
            <DasPowerDetectorChart
              config={{
                ...moduleChartConfig.dlConfig,
                title: 'Input Power (Ports 1-4)',
              }}
              onFullscreenToggle={handleCloseFullscreen}
              isFullscreen={true}
              additionalParams={moduleChartConfig.dlChartParams}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AuDeviceDetails;
